// Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
//
// This code and associated materials are protected by copyright laws and
// international treaties. Unauthorized use, reproduction, distribution, or
// modification of any content from Knowledge for Decisions (K4D) is strictly
// prohibited. For inquiries regarding the use or licensing of K4D's
// copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
// for Decisions (K4D)" and the slogan "Using data to make precise decisions"
// are registered trademarks of Knowledge for Decisions (K4D).

/**
 * The two cards the agency tab offers: buy seats, or start a conversation.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Anchor, Text, UnstyledButton } from '@/shared/ui';
import { isPlanSelectable } from '../../hooks/use-plans';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import { enabledFeatures, featureMatrix, formatStorage, type QuotaLabels } from './PlanChoice.catalog';
import { CompareTable, PlanRow } from './PlanChoice.agency';
import { planTrialDays } from './Checkout.pricing';

/** Card chrome shared by both cards, so only the emphasis differs. */
const cardStyle = (emphasised: boolean): React.CSSProperties => ({
  display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
  padding: 'var(--space-6)', borderRadius: 'var(--radius-xl)',
  background: 'var(--surface-raised)',
  border: emphasised
    ? '1.5px solid var(--seed-base)'
    : '1px solid var(--border-subtle)',
  boxShadow: emphasised ? 'var(--shadow-md)' : 'none',
});

/**
 * Pill cta.
 */
const pillCta = (primary: boolean): React.CSSProperties => ({
  height: 44, padding: '0 var(--space-5)', borderRadius: 'var(--radius-full)',
  fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  gap: 'var(--space-1-5)', cursor: 'pointer', textDecoration: 'none',
  border: `1px solid ${primary ? 'var(--seed-base)' : 'var(--text-primary)'}`,
  background: primary ? 'var(--seed-base)' : 'var(--surface-raised)',
  color: primary ? 'var(--text-inverse)' : 'var(--text-primary)',
});

/**
 * A checked list under a "what's included" heading.
 *
 * @param props - The heading and the items.
 * @returns The list.
 */
const IncludedList: React.FC<{ heading: string; items: string[] }> = ({ heading, items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)', textAlign: 'center' }}>
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
        lineHeight: 1.35,
      }}
    >
      {heading}
    </Text>
    <div
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, auto)',
        columnGap: 'var(--space-6)', rowGap: 'var(--space-2)', justifyContent: 'center',
      }}
    >
      {items.map((item) => (
        <Text
          key={item}
          component="span"
          style={{
            fontSize: 'var(--text-small)', fontWeight: 'var(--font-regular)',
            color: 'var(--text-primary)', lineHeight: 1.35,
            display: 'flex', gap: 'var(--space-2)', textAlign: 'left', textWrap: 'pretty',
          }}
        >
          <Text component="span" aria-hidden style={{ color: 'var(--status-success-text)' }}>✓</Text>
          {item}
        </Text>
      ))}
    </div>
  </div>
);

/**
 * Everything every tier carries — the promise that does not depend on the row.
 *
 * Read off the catalogue rather than listed: a feature every plan happens to
 * have today is a fact about the payload, not a sentence to maintain.
 *
 * @param plans - The agency plans on screen.
 * @param quotaLabels - The quota ladder, for the matrix.
 * @param language - The active language, for storage formatting.
 * @returns The ids of the rows on for every plan.
 */
/**
 * One compact band explaining what the auxiliary users are, and a link.
 *
 * Deliberately small, and deliberately below `IncludedList`. This card's job
 * is to sell the broker's plan; the features are what does that, and a long
 * explanation of a free extra placed above them would argue for the wrong
 * thing. What the auxiliary users can and cannot open is a list of its own
 * and belongs behind the link — printed here it would be longer than
 * everything it sits under.
 *
 * Renders nothing when no tier declares the figure. The catalogue sells
 * this, never the screen.
 *
 * The design asked for a dialog behind the link. There is no shared dialog
 * in this codebase — the few that exist are hand-rolled portals — and this
 * card already has an idiom for "show me more", the compare toggle directly
 * above. Reusing it costs nothing and adds no portal to maintain.
 */
const SupportSeatsBand: React.FC<{ plans: PlanWithFrequency[] }> = ({ plans }) => {
  const { t } = useTranslation('@pages/onboarding');
  const [open, setOpen] = useState(false);
  const brings = plans.some((plan) => (plan.limits?.seat?.support_seats ?? 0) > 0);
  if (!brings) return null;

  return (
    <div
      data-testid="plan-support-band"
      style={{
        background: 'var(--surface-card)', borderRadius: 10,
        padding: 'var(--space-3)', display: 'flex',
        flexDirection: 'column', gap: 'var(--space-1)',
      }}
    >
      <Text component="span" style={{ fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)' }}>
        {t('planChoice.agency.supportBand.title')}
      </Text>
      <Text component="span" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)' }}>
        {t('planChoice.agency.supportBand.body')}
      </Text>
      <UnstyledButton
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          alignSelf: 'flex-start', background: 'none', cursor: 'pointer',
          fontSize: 'var(--text-tag)', color: 'var(--seed-base)',
          textDecoration: 'underline',
        }}
      >
        {t(open ? 'planChoice.agency.supportBand.less' : 'planChoice.agency.supportBand.more')}
      </UnstyledButton>

      {open ? (
        <div
          data-testid="plan-support-detail"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
        >
          <div>
            <Text component="span" style={{ fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)' }}>
              {t('planChoice.agency.supportBand.opensTitle')}
            </Text>
            <Text component="p" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)', margin: 0 }}>
              {t('planChoice.agency.supportBand.opens')}
            </Text>
          </div>
          <div>
            <Text component="span" style={{ fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)' }}>
              {t('planChoice.agency.supportBand.closedTitle')}
            </Text>
            <Text component="p" style={{ fontSize: 'var(--text-tag)', color: 'var(--text-secondary)', margin: 0 }}>
              {t('planChoice.agency.supportBand.closed')}
            </Text>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function commonRowIds(
  plans: PlanWithFrequency[], quotaLabels: QuotaLabels, language: string,
): string[] {
  const perPlan = plans.map((plan) => featureMatrix(
    {
      features: enabledFeatures(plan.features),
      limits: plan.limits,
      quotas: quotaLabels[plan.key] ?? {},
    },
    { storage: (gb) => formatStorage(gb, (n) => n.toLocaleString(language)) },
  ));
  const first = perPlan[0] ?? [];
  return first
    .filter((row, index) => perPlan.every((column) => column[index]?.on))
    .map((row) => row.id);
}

/**
 * The card that sells seats: one row per tier, and the seat count comes later.
 *
 * @param props - The plans, the cycle, the quota ladder and the select handler.
 * @returns The per-seat card.
 */
export const AgencySeatCard: React.FC<{
  plans: PlanWithFrequency[];
  billingCycle: BillingCycle;
  quotaLabels: QuotaLabels;
  onSelect: (plan: PlanWithFrequency) => void;
  /** False once the owner has had a subscription: no "free days" badge. */
  trialOffered?: boolean;
}> = ({ plans, billingCycle, quotaLabels, onSelect, trialOffered = true }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const [comparing, setComparing] = useState(false);

  // The card's one CTA has to land on something buyable. Taking the
  // recommended tier unconditionally would start checkout on an announced
  // plan, which the rows below already refuse to select.
  const onSale = plans.filter(isPlanSelectable);
  const recommended = onSale.find((plan) => plan.recommended) ?? onSale[0];
  const included = commonRowIds(plans, quotaLabels, i18n.language);
  // The floor and the ceiling are the catalogue's, so raising max_seats moves
  // this sentence instead of leaving it quoting a number checkout refuses.
  // The panel sells three tiers behind one button, so the free days it can
  // promise are the fewest any of them grants -- the same `min()` the
  // checkout and Payien apply to a mixed order.
  const trialDays = onSale.length
    ? Math.min(...onSale.map((plan) => planTrialDays(plan, billingCycle)))
    : 0;
  const minSeats = recommended?.minSeats;
  const maxSeats = recommended?.users;

  return (
    <section style={cardStyle(true)} data-testid="plan-card-agency-seats">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <Text
          component="h2"
          style={{
            margin: 0, display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)',
            flexWrap: 'wrap', rowGap: 'var(--space-1-5)',
          }}
        >
          <Text
            component="span"
            style={{
              fontFamily: 'var(--font-family-alt-2)',
              fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)',
              letterSpacing: '-0.01em', lineHeight: 1.1,
              color: 'var(--text-primary)',
            }}
          >
            {t('planChoice.agency.title')}
          </Text>
          <Text
            component="span"
            style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
          >
            {t('planChoice.agency.perSeat')}
          </Text>
          {trialOffered && trialDays > 0 && (
            <Text
              component="span"
              data-testid="plan-agency-trial"
              style={{
                padding: '3px var(--space-2-5)', borderRadius: 'var(--radius-full)',
                background: 'var(--status-success-bg)', color: 'var(--status-success-text)',
                fontSize: 'var(--text-tag)', fontWeight: 'var(--font-bold)',
              }}
            >
              {t('planChoice.trial', { count: trialDays })}
            </Text>
          )}
        </Text>
        <Text
          component="p"
          style={{
            margin: 0, fontSize: 'var(--text-small)', lineHeight: 1.45,
            color: 'var(--text-secondary)', textWrap: 'pretty',
          }}
        >
          {t('planChoice.agency.intro')}
        </Text>
      </div>

      {recommended ? (
        <UnstyledButton
          type="button"
          data-testid="plan-agency-start"
          style={{ ...pillCta(true), alignSelf: 'flex-start' }}
          onClick={() => onSelect(recommended)}
        >
          {t('planChoice.agency.cta')}
          <Text component="span" aria-hidden>›</Text>
        </UnstyledButton>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {plans.map((plan) => (
          <PlanRow
            key={plan.key}
            plan={plan}
            billingCycle={billingCycle}
            quotaLabels={quotaLabels}
            onSelect={onSelect}
          />
        ))}
      </div>

      <UnstyledButton
        type="button"
        data-testid="plan-compare-toggle"
        aria-expanded={comparing}
        onClick={() => setComparing((open) => !open)}
        style={{
          alignSelf: 'flex-start', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer', color: 'var(--text-primary)',
          fontSize: 'var(--text-small)', fontWeight: 'var(--font-semibold)',
          lineHeight: 1.35,
          display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1-5)',
        }}
      >
        {t('planChoice.agency.compare')}
        <Text component="span" aria-hidden>{comparing ? '⌃' : '⌄'}</Text>
      </UnstyledButton>
      {comparing ? <CompareTable plans={plans} quotaLabels={quotaLabels} /> : null}

      <IncludedList
        heading={t('planChoice.agency.included')}
        items={included.map((id) => t(`planChoice.matrix.${id}`))}
      />

      <SupportSeatsBand plans={plans} />

      <Text
        component="span"
        style={{ fontSize: 'var(--text-tag)', color: 'var(--text-tertiary)' }}
      >
        {t('planChoice.agency.seatRange', { min: minSeats ?? 2, max: maxSeats ?? 0 })}
      </Text>
    </section>
  );
};

/** The items the enterprise card promises. Copy, not catalogue: nothing prices these. */
const ENTERPRISE_ITEMS = ['everything', 'branches', 'volume', 'onboarding', 'contract'] as const;

/**
 * The way out for teams the seat selector cannot serve.
 *
 * @param props - The catalogue's seat ceiling and where to write.
 * @returns The enterprise card.
 */
export const EnterpriseCard: React.FC<{
  maxSeats: number | undefined; href: string;
}> = ({ maxSeats, href }) => {
  const { t } = useTranslation('@pages/onboarding');

  return (
    <section style={cardStyle(false)} data-testid="plan-card-enterprise">
      <Text
        component="h2"
        style={{
          margin: 0, fontFamily: 'var(--font-family-alt-2)',
          fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)',
          letterSpacing: '-0.01em', lineHeight: 1.1,
          color: 'var(--text-primary)',
        }}
      >
        {t('planChoice.enterprise.title')}
      </Text>
      <Text
        component="p"
        style={{
          margin: 0, fontSize: 'var(--text-small)', lineHeight: 1.45,
          color: 'var(--text-secondary)', textWrap: 'pretty',
        }}
      >
        {t('planChoice.enterprise.description', { seats: maxSeats ?? 0 })}
      </Text>
      <Anchor href={href} style={{ ...pillCta(false), alignSelf: 'flex-start' }}>
        {t('planChoice.enterprise.cta')}
        <Text component="span" aria-hidden>›</Text>
      </Anchor>
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
          lineHeight: 1.35,
        }}
      >
        {t('planChoice.enterprise.customPrice')}
      </Text>
      <IncludedList
        heading={t('planChoice.agency.included')}
        items={ENTERPRISE_ITEMS.map((id) => t(`planChoice.enterprise.items.${id}`))}
      />
    </section>
  );
};

/**
 * The agency tab: the two cards, side by side until they cannot be.
 *
 * @param props - The plans, the cycle, the quota ladder, the handlers.
 * @returns The agency tab body.
 */
export const AgencyChoice: React.FC<{
  plans: PlanWithFrequency[];
  billingCycle: BillingCycle;
  quotaLabels: QuotaLabels;
  onSelect: (plan: PlanWithFrequency) => void;
  salesHref: string;
  trialOffered?: boolean;
}> = ({ plans, billingCycle, quotaLabels, onSelect, salesHref, trialOffered = true }) => (
  <div
    style={{
      width: '100%', display: 'grid', alignItems: 'start',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: 'var(--space-4)',
    }}
  >
    <AgencySeatCard
      plans={plans}
      billingCycle={billingCycle}
      quotaLabels={quotaLabels}
      onSelect={onSelect}
      trialOffered={trialOffered}
    />
    <EnterpriseCard maxSeats={plans[0]?.users} href={salesHref} />
  </div>
);

export default AgencyChoice;
